// Referencia: Modelos de UI para la vista TikTok de candidatos
// Basado en ApplyOfferCandidate de @/pages/offers/models

export interface NavigationTab {
  id: string;
  label: string;
  icon: string;
  active: boolean;
}

export interface StatusTab {
  id: string;
  label: string;
  count?: number;
}

export interface ActionButtonConfig {
  id: string;
  icon: string;
  tooltip: string;
  severity?: 'secondary' | 'danger' | 'success' | 'info';
  hasBadge?: boolean;
  badgeSeverity?: 'danger' | 'info';
  action: () => void;
  permissionConfig?: any;
}

export interface SectionState {
  minRequirements: boolean;
  desiredRequirements: boolean;
  hardSkills: boolean;
  softSkills: boolean;
  tools: boolean;
  languages: boolean;
  cv: boolean;
  interview: boolean;
  questions: boolean;
  candidateInfo: boolean;
  summary: boolean;
  experience: boolean;
  education: boolean;
  history: boolean;
}

// AI Chat Models
export type AiChatType = 'filter' | 'search' | 'killerQuestions' | 'navigate' | 'suggested' | 'wokapIA';

export interface AiMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export interface AiAssistantConfig {
  type: AiChatType;
  name: string;
  avatar: string;
  description: string;
  messages: AiMessage[];
}

// Mock conversations para cada tipo de asistente IA
export const MOCK_AI_CONVERSATIONS: Record<AiChatType, AiAssistantConfig> = {
  filter: {
    type: 'filter',
    name: 'Filtro Inteligente',
    avatar: '🔍',
    description: 'Te ayudo a filtrar candidatos de forma avanzada',
    messages: [
      {
        role: 'assistant',
        content: 'Hola! Soy tu asistente de filtrado inteligente. Puedo ayudarte a encontrar los candidatos perfectos usando criterios avanzados.',
        timestamp: new Date('2024-01-07T10:00:00'),
      },
      {
        role: 'user',
        content: 'Necesito encontrar candidatos con más de 5 años de experiencia en Angular y que vivan en Madrid',
        timestamp: new Date('2024-01-07T10:01:00'),
      },
      {
        role: 'assistant',
        content: 'Perfecto! He aplicado los siguientes filtros:\n\n✓ Experiencia: +5 años en Angular\n✓ Ubicación: Madrid\n\nEncontré 12 candidatos que cumplen estos criterios. ¿Quieres añadir algún filtro adicional?',
        timestamp: new Date('2024-01-07T10:01:30'),
      },
      {
        role: 'user',
        content: 'Sí, que también tengan conocimientos de TypeScript y disponibilidad inmediata',
        timestamp: new Date('2024-01-07T10:02:00'),
      },
      {
        role: 'assistant',
        content: 'Excelente! He refinado la búsqueda:\n\n✓ Experiencia: +5 años Angular\n✓ Ubicación: Madrid\n✓ Skill: TypeScript\n✓ Disponibilidad: Inmediata\n\nAhora tenemos 7 candidatos altamente cualificados. Los he ordenado por porcentaje de coincidencia.',
        timestamp: new Date('2024-01-07T10:02:45'),
      },
    ],
  },
  search: {
    type: 'search',
    name: 'Búsqueda Avanzada',
    avatar: '🎯',
    description: 'Encuentra candidatos específicos con búsqueda semántica',
    messages: [
      {
        role: 'assistant',
        content: '¡Hola! Soy tu asistente de búsqueda avanzada. Puedo buscar candidatos usando lenguaje natural y entender el contexto de lo que necesitas.',
        timestamp: new Date('2024-01-07T09:30:00'),
      },
      {
        role: 'user',
        content: 'Busca candidatos que hayan trabajado en startups tecnológicas y tengan experiencia liderando equipos',
        timestamp: new Date('2024-01-07T09:31:00'),
      },
      {
        role: 'assistant',
        content: 'He analizado los perfiles y encontré 15 candidatos con experiencia en startups tech:\n\n🏆 8 con rol de Tech Lead\n👥 12 con experiencia gestionando equipos (3-15 personas)\n🚀 5 con experiencia en scale-ups\n\n¿Quieres que profundice en algún sector específico?',
        timestamp: new Date('2024-01-07T09:32:00'),
      },
      {
        role: 'user',
        content: 'Enfócate en fintech y e-commerce',
        timestamp: new Date('2024-01-07T09:33:00'),
      },
      {
        role: 'assistant',
        content: 'Perfecto! He filtrado por sectores:\n\n💳 Fintech: 6 candidatos (experiencia en pagos, blockchain, banking digital)\n🛒 E-commerce: 4 candidatos (marketplaces, retail tech)\n\nTodos con track record de liderazgo técnico. ¿Los ordeno por años de experiencia o por match con la oferta?',
        timestamp: new Date('2024-01-07T09:34:00'),
      },
    ],
  },
  killerQuestions: {
    type: 'killerQuestions',
    name: 'Killer Questions AI',
    avatar: '❓',
    description: 'Genera preguntas clave para evaluar candidatos',
    messages: [
      {
        role: 'assistant',
        content: 'Hola! Soy tu asistente para crear Killer Questions. Te ayudo a diseñar preguntas estratégicas que revelen si un candidato es el fit perfecto.',
        timestamp: new Date('2024-01-07T11:00:00'),
      },
      {
        role: 'user',
        content: 'Necesito preguntas para evaluar a un Senior Frontend Developer',
        timestamp: new Date('2024-01-07T11:01:00'),
      },
      {
        role: 'assistant',
        content: 'Genial! He creado 5 killer questions enfocadas en diferentes aspectos:\n\n🎨 Arquitectura: "Explica cómo estructurarías una app React con +50 componentes"\n⚡ Performance: "Describe 3 técnicas para optimizar el render en aplicaciones complejas"\n🔧 Problema real: "¿Cómo debuguearías un memory leak en producción?"\n👥 Liderazgo: "Cuenta cómo has mentorizado juniors en buenas prácticas"\n📊 Toma de decisiones: "React vs Vue vs Angular: ¿cuándo usarías cada uno?"\n\n¿Quieres que añada alguna pregunta sobre un área específica?',
        timestamp: new Date('2024-01-07T11:02:30'),
      },
      {
        role: 'user',
        content: 'Añade una sobre testing',
        timestamp: new Date('2024-01-07T11:03:00'),
      },
      {
        role: 'assistant',
        content: 'Perfecto! He añadido:\n\n🧪 Testing: "Describe tu estrategia de testing (unitarios, integración, E2E). ¿Qué herramientas usas y por qué?"\n\nEsta pregunta revela no solo conocimiento técnico sino también madurez profesional y visión de calidad. ¿Envío estas preguntas a los candidatos seleccionados?',
        timestamp: new Date('2024-01-07T11:03:45'),
      },
    ],
  },
  navigate: {
    type: 'navigate',
    name: 'Navegador woKap',
    avatar: '🧭',
    description: 'Explora y descubre funcionalidades de la plataforma',
    messages: [
      {
        role: 'assistant',
        content: '¡Bienvenido! Soy tu guía por woKap. Puedo ayudarte a descubrir funcionalidades, navegar por la plataforma y optimizar tu flujo de trabajo.',
        timestamp: new Date('2024-01-07T14:00:00'),
      },
      {
        role: 'user',
        content: '¿Cómo puedo ver las métricas de mis ofertas activas?',
        timestamp: new Date('2024-01-07T14:01:00'),
      },
      {
        role: 'assistant',
        content: 'Te puedo llevar a varias secciones:\n\n📊 Dashboard: Vista general con KPIs principales\n📈 Análisis por oferta: Métricas detalladas (conversión, tiempo medio, fuentes)\n📋 Reportes: Exportar datos y comparativas\n\n¿Prefieres ver el dashboard general o profundizar en una oferta específica?',
        timestamp: new Date('2024-01-07T14:02:00'),
      },
      {
        role: 'user',
        content: 'Muéstrame las métricas de la oferta actual',
        timestamp: new Date('2024-01-07T14:03:00'),
      },
      {
        role: 'assistant',
        content: 'Aquí están las métricas de tu oferta:\n\n👥 Total candidatos: 47\n✅ Tasa conversión: 68% (inscrito → proceso)\n⏱️ Tiempo medio respuesta: 2.3 días\n🎯 Top fuente: LinkedIn (42%)\n⭐ Rating promedio: 4.2/5\n\n¿Quieres que te muestre cómo comparar estas métricas con otras ofertas similares?',
        timestamp: new Date('2024-01-07T14:04:00'),
      },
    ],
  },
  suggested: {
    type: 'suggested',
    name: 'Recomendador IA',
    avatar: '✨',
    description: 'Sugiere candidatos ideales usando machine learning',
    messages: [
      {
        role: 'assistant',
        content: '¡Hola! Soy tu asistente de recomendaciones inteligentes. Analizo miles de perfiles para sugerirte candidatos que quizás no has considerado pero tienen alto potencial.',
        timestamp: new Date('2024-01-07T15:00:00'),
      },
      {
        role: 'user',
        content: '¿Tienes candidatos recomendados para esta oferta?',
        timestamp: new Date('2024-01-07T15:01:00'),
      },
      {
        role: 'assistant',
        content: 'He analizado +500 perfiles y encontré 8 candidatos altamente compatibles que aún no están inscritos:\n\n🌟 3 candidatos con 95%+ de match\n💎 2 perfiles de empresas similares\n🎯 3 con skills exactos pero sin aplicar\n\nEstos candidatos tienen alta probabilidad de aceptar basado en patrones históricos. ¿Quieres que te muestre el top 3?',
        timestamp: new Date('2024-01-07T15:02:00'),
      },
      {
        role: 'user',
        content: 'Sí, muéstrame el top 3',
        timestamp: new Date('2024-01-07T15:03:00'),
      },
      {
        role: 'assistant',
        content: 'Top 3 recomendaciones:\n\n🥇 María López - 97% match\n   • 8 años Angular + TypeScript\n   • Ex-senior en startup similar\n   • Busca activamente (visto hace 2 días)\n\n🥈 Carlos Ruiz - 96% match\n   • Lead Developer, stack idéntico\n   • Ubicación: Madrid (tu preferencia)\n   • Disponibilidad: 1 mes\n\n🥉 Ana Martín - 95% match\n   • Freelance → buscando estabilidad\n   • Portfolio impresionante\n   • Ha aplicado a ofertas similares\n\n¿Quiero enviarles una invitación personalizada?',
        timestamp: new Date('2024-01-07T15:04:30'),
      },
    ],
  },
  wokapIA: {
    type: 'wokapIA',
    name: 'wokapIA',
    avatar: '🤖',
    description: 'Asistente general de reclutamiento inteligente',
    messages: [
      {
        role: 'assistant',
        content: '¡Hola! Soy wokapIA, tu asistente de reclutamiento inteligente. Puedo ayudarte con cualquier aspecto del proceso: desde evaluar candidatos hasta optimizar tus ofertas.',
        timestamp: new Date('2024-01-07T16:00:00'),
      },
      {
        role: 'user',
        content: '¿Qué opinas de este candidato que estoy viendo?',
        timestamp: new Date('2024-01-07T16:01:00'),
      },
      {
        role: 'assistant',
        content: 'He analizado el perfil completo:\n\n✅ **Fortalezas:**\n• Skills técnicas muy alineadas (90% match)\n• Experiencia en sector relevante\n• Buena progresión de carrera\n• Respuestas de entrevista coherentes\n\n⚠️ **Áreas de atención:**\n• Ubicación requiere relocalización\n• Expectativas salariales 15% sobre presupuesto\n• Disponibilidad en 2 meses\n\n💡 **Recomendación:** Candidato fuerte. Sugiero moverlo a "Preseleccionados" y negociar condiciones. ¿Quieres que prepare preguntas específicas para la siguiente fase?',
        timestamp: new Date('2024-01-07T16:02:30'),
      },
      {
        role: 'user',
        content: 'Sí, prepara preguntas para evaluar mejor su experiencia en liderazgo',
        timestamp: new Date('2024-01-07T16:03:00'),
      },
      {
        role: 'assistant',
        content: 'He generado 4 preguntas clave sobre liderazgo:\n\n1️⃣ "Describe una situación donde tuviste que gestionar un conflicto técnico en tu equipo. ¿Cómo lo resolviste?"\n\n2️⃣ "¿Cómo balanceas el desarrollo técnico con las responsabilidades de mentoría?"\n\n3️⃣ "Cuéntame sobre una decisión técnica importante que tomaste y cómo la comunicaste al equipo"\n\n4️⃣ "¿Qué métricas usas para medir el éxito de tu equipo más allá del código entregado?"\n\nEstas preguntas revelarán su madurez como líder técnico. ¿Las envío como preguntas directas o prefieres usarlas en la entrevista final?',
        timestamp: new Date('2024-01-07T16:04:15'),
      },
    ],
  },
};

